import React, { useState, useRef, useEffect } from 'react';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { verifyPIN, initializeVault, encryptData, getVaultKey, setVaultKey } from '../utils/vaultCrypto';
import { toast } from 'sonner';

const ChangePIN = ({ isOpen, onClose, currentSalt, testValue, onPINChanged }) => {
  const [step, setStep] = useState('current'); // current, new, confirm
  const [currentPin, setCurrentPin] = useState(['', '', '', '', '', '']);
  const [newPin, setNewPin] = useState(['', '', '', '', '', '']);
  const [confirmPin, setConfirmPin] = useState(['', '', '', '', '', '']);
  const [showPin, setShowPin] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const currentRefs = useRef([]);
  const newRefs = useRef([]);
  const confirmRefs = useRef([]);

  useEffect(() => {
    if (isOpen) {
      setStep('current');
      setCurrentPin(['', '', '', '', '', '']);
      setNewPin(['', '', '', '', '', '']);
      setConfirmPin(['', '', '', '', '', '']);
      setError('');
      setTimeout(() => currentRefs.current[0]?.focus(), 100);
    }
  }, [isOpen]);

  useEffect(() => {
    if (step === 'new') {
      setTimeout(() => newRefs.current[0]?.focus(), 100);
    }
    if (step === 'confirm') {
      setTimeout(() => confirmRefs.current[0]?.focus(), 100);
    }
  }, [step]);

  const handlePinChange = (index, value, pinType) => {
    if (!/^\d*$/.test(value)) return;
    
    const setters = { current: setCurrentPin, new: setNewPin, confirm: setConfirmPin };
    const pins = { current: currentPin, new: newPin, confirm: confirmPin };
    const refs = { current: currentRefs, new: newRefs, confirm: confirmRefs };
    
    const newPinArray = [...pins[pinType]];
    newPinArray[index] = value.slice(-1);
    setters[pinType](newPinArray);
    setError('');
    
    if (value && index < 5) {
      refs[pinType].current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e, pinType) => {
    const pins = { current: currentPin, new: newPin, confirm: confirmPin };
    const refs = { current: currentRefs, new: newRefs, confirm: confirmRefs };
    
    if (e.key === 'Backspace' && !pins[pinType][index] && index > 0) {
      refs[pinType].current[index - 1]?.focus();
    }
  };

  const verifyCurrentPIN = async () => {
    const pinString = currentPin.join('');
    if (pinString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    setLoading(true);
    try {
      const isValid = await verifyPIN(pinString, currentSalt, testValue);
      if (!isValid) {
        setError('Incorrect current PIN');
        setCurrentPin(['', '', '', '', '', '']);
        currentRefs.current[0]?.focus();
      } else {
        setStep('new');
      }
    } catch {
      setError('Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const validateNewPIN = () => {
    const pinString = newPin.join('');
    if (pinString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }
    
    const weakPins = ['000000', '111111', '222222', '333333', '444444', '555555', 
                      '666666', '777777', '888888', '999999', '123456', '654321'];
    if (weakPins.includes(pinString)) {
      setError('Please choose a stronger PIN');
      return;
    }
    
    if (pinString === currentPin.join('')) {
      setError('New PIN must be different from current PIN');
      return;
    }
    
    setStep('confirm');
  };

  const completeChange = async () => {
    const newPinString = newPin.join('');
    const confirmPinString = confirmPin.join('');
    
    if (newPinString !== confirmPinString) {
      setError('PINs do not match');
      setConfirmPin(['', '', '', '', '', '']);
      confirmRefs.current[0]?.focus();
      return;
    }
    
    setLoading(true);
    try {
      // Create new vault with new PIN
      const { salt: newSalt, key: newKey } = await initializeVault(newPinString);
      
      // Create new test value
      const newTestValue = await encryptData('vault_test_' + Date.now(), newKey);
      
      // Update in memory
      setVaultKey(newKey);
      
      // Callback to parent with new vault info
      onPINChanged({
        salt: newSalt,
        testValue: newTestValue,
        key: newKey
      });
      
      toast.success('PIN changed successfully!');
      onClose();
    } catch (err) {
      setError('Failed to change PIN. Please try again.');
      console.error('Change PIN error:', err);
    } finally {
      setLoading(false);
    }
  };

  const PinInputGroup = ({ pinState, refs, pinType }) => (
    <div className="flex justify-center gap-2">
      {pinState.map((digit, index) => (
        <input
          key={index}
          ref={el => refs.current[index] = el}
          type={showPin ? 'text' : 'password'}
          inputMode="numeric"
          pattern="[0-9]*"
          maxLength={1}
          value={digit}
          onChange={(e) => handlePinChange(index, e.target.value, pinType)}
          onKeyDown={(e) => handleKeyDown(index, e, pinType)}
          onFocus={(e) => e.target.select()}
          onInput={(e) => {
            const val = e.target.value;
            if (val.length > 0 && /^\d$/.test(val.slice(-1))) {
              handlePinChange(index, val.slice(-1), pinType);
            }
          }}
          onClick={(e) => {
            const firstEmptyIndex = pinState.findIndex(d => d === '');
            if (firstEmptyIndex !== -1 && firstEmptyIndex !== index) {
              e.preventDefault();
              refs.current[firstEmptyIndex]?.focus();
            }
          }}
          autoComplete="one-time-code"
          className="w-10 h-12 text-center text-xl font-bold border-2 border-slate-200 rounded-lg 
                     focus:border-[#0D2340] focus:ring-2 focus:ring-[#0D2340]/20 outline-none"
          disabled={loading}
          data-testid={`change-pin-${pinType}-${index}`}
        />
      ))}
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="h-5 w-5 text-[#0D2340]" />
            Change Vault PIN
          </DialogTitle>
          <DialogDescription>
            {step === 'current' && 'Enter your current PIN to continue'}
            {step === 'new' && 'Enter your new 6-digit PIN'}
            {step === 'confirm' && 'Confirm your new PIN'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {step === 'current' && (
            <>
              <PinInputGroup pinState={currentPin} refs={currentRefs} pinType="current" />
              <Button onClick={verifyCurrentPIN} className="w-full" disabled={loading}>
                {loading ? 'Verifying...' : 'Continue'}
              </Button>
            </>
          )}

          {step === 'new' && (
            <>
              <PinInputGroup pinState={newPin} refs={newRefs} pinType="new" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('current')} className="flex-1">
                  Back
                </Button>
                <Button onClick={validateNewPIN} className="flex-1">
                  Next
                </Button>
              </div>
            </>
          )}

          {step === 'confirm' && (
            <>
              <PinInputGroup pinState={confirmPin} refs={confirmRefs} pinType="confirm" />
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setStep('new')} className="flex-1">
                  Back
                </Button>
                <Button onClick={completeChange} className="flex-1" disabled={loading}>
                  {loading ? 'Changing...' : 'Change PIN'}
                </Button>
              </div>
            </>
          )}

          <button
            onClick={() => setShowPin(!showPin)}
            className="flex items-center justify-center gap-2 text-sm text-slate-500 mx-auto"
          >
            {showPin ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            {showPin ? 'Hide' : 'Show'}
          </button>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-500 text-sm">
              <AlertCircle className="h-4 w-4" />
              {error}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ChangePIN;
